import * as React from 'react';

import {safelyGetGraphQLErrorMessage} from 'nutshell-core/utils/graphql-errors';

import {ColorSwatchPicker} from 'shells/color-swatch-picker/color-swatch-picker';
import {getColorNameFromTagColorDark, getTagColorSwatches} from 'shells/colors/helpers';
import {LegacySetupTableEmptyState} from 'shells/table/legacy-setup-table-empty-state';
import {LegacySetupTableWrapper} from 'shells/table/legacy-setup-table-wrapper';
import {SetupItemTable} from 'shells/table/setup-item-table';
import type {Row, SortBy} from 'shells/table/types';

import type {TagType} from 'nutshell-graphql-types';
import type {TagBasic} from 'nutshell-graphql-types';

import {addNotification} from '../../../notifications/index';

import {useCreateTag, useUpdateTag, useDeleteTag, useRestoreTag} from '../../core/select/hooks';
import {translateTagTypeToHumanReadable} from '../../core/tags/helpers';

import {EditCreateTagPopover} from './edit-create-tag-popover';
import {getReportUrl} from './helpers';
import {TagRowActionMenu} from './tag-row-action-menu';

import styles from './tags-table.module.css';

type Props = {
    tagType: TagType;
    tags: TagBasic[];
    tagCount: number | null | undefined;
    isLoading: boolean;
    errorMessage: string | null | undefined;
    sortBy?: SortBy[];
    hasNextPage: boolean;
    isViewingArchived?: boolean;
    onRefetch: () => void;
    onChangeSortBy?: (sortBy: SortBy[]) => void;
    fetchMore: (searchTerm: string, tagType: TagType, shouldPaginate?: boolean) => Promise<any>;
};

export function TagsTable(props: Props) {
    const {tags, hasNextPage, fetchMore, isLoading, errorMessage, tagType} = props;
    const [isAddingTag, setIsAddingTag] = React.useState<boolean>(false);
    const [isEditingTag, setIsEditingTag] = React.useState<TagBasic | null | undefined>(null);
    const [isSaving, setIsSaving] = React.useState<boolean>(false);
    const [editCreateErrorMessage, setEditCreateErrorMessage] = React.useState<
        string | null | undefined
    >(null);
    const [tableHeight, setTableHeight] = React.useState<number>(0);
    const tableRef = React.useRef(null);
    const addButtonRef = React.useRef<HTMLDivElement | null | undefined>(null);
    const rowRef = React.useRef<HTMLDivElement | null | undefined>(null);

    React.useLayoutEffect(() => {
        if (tableRef && tableRef.current) {
            const visibleRows = Math.max(0, Math.min(tags.length, 10));
            const height = visibleRows * 38; // 38 is itemSize below
            setTableHeight(height);
        }
    }, [tableRef, tags.length]);

    // Our initial query fetches 1000 tags, we need to fetch more if not all the tags are loaded
    React.useEffect(() => {
        if (hasNextPage) {
            fetchMore('', tagType, true);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [hasNextPage, tagType, tags.length]);

    const {createTag} = useCreateTag();
    const {updateTag} = useUpdateTag();
    const {deleteTag, isDeleting} = useDeleteTag();
    const {restoreTag, isLoading: isRestoring} = useRestoreTag();
    const onPopoverClose = () => {
        setEditCreateErrorMessage(null);
        setIsSaving(false);
        setIsAddingTag(false);
        setIsEditingTag(null);
    };

    const handleCreateTag = (input: {name: string; color: string}) => {
        setEditCreateErrorMessage(null);
        setIsSaving(true);

        createTag({
            tag: {
                name: input.name,
                entityType: tagType,
                // @ts-expect-error
                color: input.color,
            },
        })
            .then(onPopoverClose)
            .catch((e) => {
                setEditCreateErrorMessage(
                    safelyGetGraphQLErrorMessage(e) ||
                        'We encountered a problem while creating the tag. Please try again.'
                );
                setIsSaving(false);
            });
    };
    const handleDeleteTag = (id: string) => {
        deleteTag({tagId: id})
            .then(() => {
                props.onRefetch();
            })
            .catch(() => {
                addNotification('Something went wrong', {
                    icon: 'frowny',
                    body: 'We encountered a problem while deleting the tag. Please try again.',
                });
            });
    };

    const handleRestoreTag = (id: string) => {
        restoreTag({tagId: id})
            .then(() => {
                props.onRefetch();
            })
            .catch(() => {
                addNotification('Something went wrong', {
                    icon: 'frowny',
                    body: 'We encountered a problem while restoring the tag. Please try again.',
                });
            });
    };

    const handleUpdateTag = React.useCallback(
        (input: {id?: string; name: string; color: string}) => {
            setEditCreateErrorMessage(null);
            setIsSaving(true);

            const id = input.id || (isEditingTag && isEditingTag.id);
            // @ts-expect-error
            updateTag({tag: {id, name: input.name, color: input.color}})
                .then(onPopoverClose)
                .catch((e) => {
                    setEditCreateErrorMessage(
                        safelyGetGraphQLErrorMessage(e) ||
                            'We encountered a problem while updating the tag. Please try again.'
                    );
                    setIsSaving(false);
                });
        },
        [isEditingTag, updateTag]
    );

    const renderRowActionMenu = (row: Row<any>) => (
        <TagRowActionMenu
            onDeleteTag={() => handleDeleteTag(row.original.id)}
            onRestoreTag={
                props.isViewingArchived
                    ? () => {
                          handleRestoreTag(row.original.id);
                      }
                    : undefined
            }
            isArchivedView={props.isViewingArchived}
            reportUrl={getReportUrl(props.tagType, row.original.id)}
        />
    );

    const shouldShowLoading = isLoading || isDeleting || isRestoring;

    return (
        // @ts-expect-error
        <div ref={(r) => (tableRef.current = r)} className={styles.container}>
            <LegacySetupTableWrapper title='Tags' count={props.tagCount}>
                <SetupItemTable
                    items={tags.map((tag) => ({
                        id: tag.id,
                        name: tag.name,
                        color: tag.color,
                        count: tag.count,
                        createdTime: tag.createdTime,
                        lastUsedTime: tag.lastUsedTime,
                        creator: tag.creator,
                        isDeleteable: true,
                        isEditable: true,
                    }))}
                    title='Tag'
                    isLoading={shouldShowLoading}
                    error={errorMessage}
                    getAddButtonRef={(ref) => {
                        addButtonRef.current = ref;
                    }}
                    getRowRef={(ref) => {
                        rowRef.current = ref;
                    }}
                    onAddRowClick={() => {
                        setIsAddingTag(true);
                    }}
                    onSelectRow={(row) => setIsEditingTag(row.original)}
                    // @ts-expect-error
                    onDelete={(tagId: string) => {
                        handleDeleteTag(tagId);
                    }}
                    onRestore={
                        props.isViewingArchived ? (tagId: string) => restoreTag({tagId}) : undefined
                    }
                    sortBy={props.sortBy}
                    onChangeSortBy={props.onChangeSortBy}
                    emptyState={
                        <LegacySetupTableEmptyState
                            text={`No ${translateTagTypeToHumanReadable(tagType)} tags found`}
                        />
                    }
                    virtualizeProps={{
                        height: tableHeight,
                        itemSize: 38,
                    }}
                    renderRowActionMenu={renderRowActionMenu}
                    additionalColumns={React.useMemo(
                        () => [
                            {
                                column: {
                                    id: 'color',
                                    Header: 'Color',
                                    accessor: (tag: TagBasic) => tag,
                                    dataType: 'component',
                                    width: 50,
                                    // @ts-expect-error
                                    sortType: (rowA, rowB) => {
                                        if (rowA.original.color === 'COLOR_DEFAULT') {
                                            return 1;
                                        }

                                        if (rowB.original.color === 'COLOR_DEFAULT') {
                                            return -1;
                                        }

                                        return rowA.original.color > rowB.original.color ? -1 : 1;
                                    },
                                    centerAlign: true,
                                    Cell: ({cell}: any) => (
                                        <ColorSwatchPicker
                                            colorSwatches={getTagColorSwatches()}
                                            selectedSwatch={{
                                                id: cell.value.color,
                                                colorName: getColorNameFromTagColorDark(
                                                    cell.value.color
                                                ),
                                            }}
                                            onSelectSwatch={(selectedSwatch) => {
                                                handleUpdateTag({
                                                    id: cell.value.id,
                                                    name: cell.value.name,
                                                    color: selectedSwatch.id,
                                                });
                                            }}
                                            buttonSize={20}
                                        />
                                    ),
                                },
                                index: 1,
                            },
                            {
                                column: {
                                    id: 'count',
                                    Header: 'Count',
                                    // @ts-expect-error
                                    accessor: (row) => row.count.toLocaleString(),
                                    dataType: 'string',
                                    width: 100,
                                },
                                index: 2,
                            },
                            {
                                column: {
                                    id: 'creator',
                                    Header: 'Created by',
                                    // @ts-expect-error
                                    accessor: (row) => row.creator,
                                    dataType: 'linkedEntity',
                                },
                                index: 3,
                            },
                        ],
                        [handleUpdateTag]
                    )}
                />
            </LegacySetupTableWrapper>
            {isAddingTag && (
                <EditCreateTagPopover
                    anchor={addButtonRef.current}
                    onClose={onPopoverClose}
                    isEditing={false}
                    isSaving={isSaving}
                    onSave={handleCreateTag}
                    errorMessage={editCreateErrorMessage || ''}
                />
            )}
            {isEditingTag && (
                <EditCreateTagPopover
                    anchor={rowRef.current}
                    isEditing={true}
                    name={isEditingTag.name}
                    color={isEditingTag.color}
                    onClose={onPopoverClose}
                    isSaving={isSaving}
                    errorMessage={editCreateErrorMessage || ''}
                    onSave={handleUpdateTag}
                />
            )}
        </div>
    );
}
